/**
 * GeoService
 *
 * Approximate, request-based geolocation from our own Vercel Function (api/geo.js).
 * The visitor's IP never goes to a third party. Both the consent banner (country) and the
 * first verdict (approximate area) use it, so the request is made at most once per page load.
 */

const GEO_ENDPOINT = '/api/geo';
const GEO_TIMEOUT = 3000;

let pendingRequest = null;

const fetchRequestGeo = async () => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), GEO_TIMEOUT);

  try {
    const response = await fetch(GEO_ENDPOINT, { signal: controller.signal });
    if (!response.ok) {
      throw new Error(`Geo lookup failed with status ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    // Expected locally (no functions in `npm run dev`) and when blocked or offline.
    console.log('[Geo] Lookup unavailable:', error.name === 'AbortError' ? 'timed out' : error.message);
    return null;
  } finally {
    clearTimeout(timeoutId);
  }
};

/**
 * @returns {Promise<{ country, city, latitude, longitude } | null>} null if the lookup failed
 */
export const getRequestGeo = () => {
  pendingRequest ??= fetchRequestGeo();
  return pendingRequest;
};

/**
 * Approximate (city-level) location for the current visitor, or null if unknown.
 * @returns {Promise<{ latitude, longitude } | null>}
 */
export const getApproximateLocation = async () => {
  const geo = await getRequestGeo();
  if (!Number.isFinite(geo?.latitude) || !Number.isFinite(geo?.longitude)) return null;
  return { latitude: geo.latitude, longitude: geo.longitude };
};

/** Test helper: forget the cached lookup. */
export const resetRequestGeoForTests = () => {
  pendingRequest = null;
};
